const DESKTOP_POINTER_QUERY = '(hover: hover) and (pointer: fine)';
const RESPONSIVE_MEDIA_PATTERN = /\(\s*(?:min|max)-(?:width|height)\s*:/i;
const ZOOM_DPR_TOLERANCE = 0.015;
const STARTING_DEVICE_PIXEL_RATIO = typeof window !== 'undefined'
    ? window.devicePixelRatio || 1
    : 1;

const matchesMedia = (query) => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(query).matches
);

export const isDesktopPointerDevice = () => (
    matchesMedia(DESKTOP_POINTER_QUERY)
);

export const isMobileDeviceViewport = () => (
    matchesMedia('(max-width: 768px)')
    && !isDesktopPointerDevice()
);

export const isDesktopBrowserZoomActive = () => (
    isDesktopPointerDevice()
    && Math.abs((window.devicePixelRatio || 1) - STARTING_DEVICE_PIXEL_RATIO) > ZOOM_DPR_TOLERANCE
);

/*
 * Chrome browser zoom changes the CSS viewport and therefore crosses every
 * responsive breakpoint during a smooth zoom animation. On the portal that
 * used to swap the wide widget rails, desktop feed, tablet layout and mobile
 * panel several times in one gesture.
 *
 * Keep the media-query results from the last unzoomed desktop viewport while
 * devicePixelRatio differs from its starting value. The declarations are not
 * copied or changed, so the rendered design remains exactly the same; only
 * the breakpoint churn is paused until the user returns to the starting zoom.
 * Touch/mobile devices are deliberately excluded.
 */
export const installStableDesktopZoom = () => {
    if (
        typeof window === 'undefined'
        || typeof document === 'undefined'
        || !isDesktopPointerDevice()
        || window.__jumptakeStableDesktopZoomInstalled
    ) {
        return;
    }

    window.__jumptakeStableDesktopZoomInstalled = true;

    const readLayoutWidth = () => (
        document.getElementById('root')?.getBoundingClientRect().width
        || document.body?.getBoundingClientRect().width
        || document.documentElement.clientWidth
        || window.innerWidth
    );

    let responsiveRules = [];
    let layoutLocked = false;
    let refreshFrame = 0;
    let stableLayoutWidth = readLayoutWidth();
    let anchoredLayers = [];
    let stableLayerSnapshots = new Map();

    const getLayerAnchor = (element) => (
        element.classList.contains('portal-side-widgets')
            ? element.closest('.home-page')
            : element.closest('.app-container')
    );

    const measureViewportLayer = (element) => {
        const rect = element.getBoundingClientRect();
        const anchor = getLayerAnchor(element);
        const anchorRect = anchor?.getBoundingClientRect() || {
            top: -window.scrollY,
            left: -window.scrollX
        };

        return {
            top: rect.top - anchorRect.top + (anchor?.scrollTop || 0),
            left: rect.left - anchorRect.left + (anchor?.scrollLeft || 0),
            width: rect.width,
            height: rect.height
        };
    };

    const updateStableLayoutMetrics = () => {
        if (layoutLocked || isDesktopBrowserZoomActive()) return;
        stableLayoutWidth = readLayoutWidth();
        stableLayerSnapshots = new Map(Array.from(document.querySelectorAll(
            '#portal-public-header, .jt-home-header, .portal-side-widgets'
        )).map((element) => [element, measureViewportLayer(element)]));
    };

    const captureViewportLayers = () => {
        document.documentElement.style.setProperty('--jt-zoom-layout-width', `${stableLayoutWidth}px`);
        anchoredLayers = Array.from(document.querySelectorAll(
            '#portal-public-header, .jt-home-header, .portal-side-widgets'
        )).map((element) => {
            const snapshot = stableLayerSnapshots.get(element) || measureViewportLayer(element);
            element.style.setProperty('--jt-zoom-layer-top', `${snapshot.top}px`);
            element.style.setProperty('--jt-zoom-layer-left', `${snapshot.left}px`);
            element.style.setProperty('--jt-zoom-layer-width', `${snapshot.width}px`);
            element.style.setProperty('--jt-zoom-layer-height', `${snapshot.height}px`);
            return element;
        });
    };

    const releaseViewportLayers = () => {
        anchoredLayers.forEach((element) => {
            element.style.removeProperty('--jt-zoom-layer-top');
            element.style.removeProperty('--jt-zoom-layer-left');
            element.style.removeProperty('--jt-zoom-layer-width');
            element.style.removeProperty('--jt-zoom-layer-height');
        });
        anchoredLayers = [];
        document.documentElement.style.removeProperty('--jt-zoom-layout-width');
    };

    const visitRules = (rules, records) => {
        Array.from(rules || []).forEach((rule) => {
            const mediaText = rule?.media?.mediaText;

            if (typeof mediaText === 'string' && RESPONSIVE_MEDIA_PATTERN.test(mediaText)) {
                records.push({
                    rule,
                    mediaText,
                    matches: window.matchMedia(mediaText).matches
                });
            }

            if (rule?.cssRules) {
                try {
                    visitRules(rule.cssRules, records);
                } catch (error) {
                    // A browser-owned or cross-origin nested sheet is not editable.
                }
            }
        });
    };

    const collectResponsiveRules = () => {
        const records = [];

        Array.from(document.styleSheets || []).forEach((sheet) => {
            try {
                visitRules(sheet.cssRules, records);
            } catch (error) {
                // Cross-origin stylesheets are intentionally left untouched.
            }
        });

        responsiveRules = records;
    };

    const setLayoutLocked = (shouldLock) => {
        if (layoutLocked === shouldLock) return;

        if (shouldLock) captureViewportLayers();

        responsiveRules.forEach(({ rule, mediaText, matches }) => {
            try {
                rule.media.mediaText = shouldLock
                    ? (matches ? 'all' : 'not all')
                    : mediaText;
            } catch (error) {
                // Ignore a stylesheet that became unavailable after collection.
            }
        });

        layoutLocked = shouldLock;
        document.documentElement.toggleAttribute('data-jt-desktop-zoom-locked', shouldLock);
        if (!shouldLock) releaseViewportLayers();
    };

    const refreshAtStableZoom = () => {
        if (layoutLocked || refreshFrame) return;

        refreshFrame = window.requestAnimationFrame(() => {
            refreshFrame = 0;
            updateStableLayoutMetrics();
            collectResponsiveRules();
        });
    };

    const syncZoomState = () => {
        const currentDpr = window.devicePixelRatio || 1;
        const isAwayFromStartingZoom = Math.abs(currentDpr - STARTING_DEVICE_PIXEL_RATIO) > ZOOM_DPR_TOLERANCE;

        if (isAwayFromStartingZoom) {
            setLayoutLocked(true);
            return;
        }

        if (layoutLocked) {
            setLayoutLocked(false);
            collectResponsiveRules();
            return;
        }

        refreshAtStableZoom();
    };

    collectResponsiveRules();
    updateStableLayoutMetrics();
    if (typeof ResizeObserver === 'function') {
        const layoutObserver = new ResizeObserver(updateStableLayoutMetrics);
        layoutObserver.observe(document.documentElement);
        if (document.body) layoutObserver.observe(document.body);
        const root = document.getElementById('root');
        if (root) layoutObserver.observe(root);
    }
    if (typeof MutationObserver === 'function') {
        let metricsFrame = 0;
        const layoutMutationObserver = new MutationObserver(() => {
            if (layoutLocked || isDesktopBrowserZoomActive() || metricsFrame) return;
            metricsFrame = window.requestAnimationFrame(() => {
                metricsFrame = 0;
                updateStableLayoutMetrics();
            });
        });
        const root = document.getElementById('root');
        if (root) layoutMutationObserver.observe(root, { childList: true, subtree: true });
    }
    window.addEventListener('resize', syncZoomState, { passive: true });
    window.visualViewport?.addEventListener('resize', syncZoomState, { passive: true });
    window.addEventListener('load', refreshAtStableZoom, { once: true });
};
